const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';

/**
 * Always read token fresh from localStorage.
 * This avoids stale closures when token gets refreshed.
 */
function getToken() {
  return localStorage.getItem('keycloak_token');
}

async function request(path, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  let res;
  try {
    res = await fetch(`${BASE}${path}`, { ...options, headers });
  } catch (err) {
    throw new Error(`Network error: ${err.message}`);
  }

  // If 401, try to refresh the token once and retry
  if (res.status === 401) {
    const refreshed = await tryRefreshToken();
    if (refreshed) {
      const newToken = getToken();
      const retryHeaders = {
        ...headers,
        Authorization: `Bearer ${newToken}`,
      };
      res = await fetch(`${BASE}${path}`, { ...options, headers: retryHeaders });
    }
  }

  if (!res.ok) {
    // If still 401 after refresh attempt, redirect to login
    if (res.status === 401) {
      localStorage.removeItem('keycloak_token');
      localStorage.removeItem('keycloak_refresh_token');
      window.location.href = '/login';
      return;
    }
    const text = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${text}`);
  }

  if (res.status === 204) return null;
  return res.json();
}

/**
 * Try to refresh the access token using the refresh_token.
 * Returns true if successful, false otherwise.
 */
async function tryRefreshToken() {
  const refreshToken = localStorage.getItem('keycloak_refresh_token');
  if (!refreshToken) return false;

  try {
    const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:9090';
    const resp = await fetch(
      `${keycloakUrl}/realms/fbmirror/protocol/openid-connect/token`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: 'fbmirror-frontend',
          refresh_token: refreshToken,
        }),
      }
    );

    if (!resp.ok) {
      console.warn('Token refresh failed:', resp.status);
      return false;
    }

    const data = await resp.json();
    localStorage.setItem('keycloak_token', data.access_token);
    if (data.refresh_token) {
      localStorage.setItem('keycloak_refresh_token', data.refresh_token);
    }
    return true;
  } catch (err) {
    console.error('Token refresh error:', err);
    return false;
  }
}

// Scripts
export const getScripts = () => request('/api/v1/scripts');
export const createScript = (data) => request('/api/v1/scripts', { method: 'POST', body: JSON.stringify(data) });
export const updateScript = (id, data) => request(`/api/v1/scripts/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const toggleScript = (id) => request(`/api/v1/scripts/${id}/toggle`, { method: 'PATCH' });
export const deleteScript = (id) => request(`/api/v1/scripts/${id}`, { method: 'DELETE' });

// Pixels
export const getPixels = () => request('/api/v1/pixels');
export const createPixel = (data) => request('/api/v1/pixels', { method: 'POST', body: JSON.stringify(data) });
export const updatePixel = (id, data) => request(`/api/v1/pixels/${id}`, { method: 'PUT', body: JSON.stringify(data) });
export const togglePixel = (id) => request(`/api/v1/pixels/${id}/toggle`, { method: 'PATCH' });
export const deletePixel = (id) => request(`/api/v1/pixels/${id}`, { method: 'DELETE' });

// Mappings
export const getMappings = (scriptId) => request(`/api/v1/mappings${scriptId ? `?scriptId=${scriptId}` : ''}`);
export const createMapping = (data) => request('/api/v1/mappings', { method: 'POST', body: JSON.stringify(data) });
export const toggleMapping = (id) => request(`/api/v1/mappings/${id}/toggle`, { method: 'PATCH' });
export const deleteMapping = (id) => request(`/api/v1/mappings/${id}`, { method: 'DELETE' });

// Event Logs
export const getEventLogs = (scriptId, page = 0, size = 50) =>
  request(`/api/v1/events?${scriptId ? `scriptId=${scriptId}&` : ''}page=${page}&size=${size}`);

// Snippet URL
export const getSnippetUrl = (scriptId) =>
  `${BASE}/api/v1/script/${scriptId}/snippet.js`;

// Admin
export const getAdminMe = () => request('/api/v1/admin/me');
export const getAdminBuyers = () => request('/api/v1/admin/buyers');
export const createAdminBuyer = (data) => request('/api/v1/admin/buyers', { method: 'POST', body: JSON.stringify(data) });
export const deleteAdminBuyer = (id) => request(`/api/v1/admin/buyers/${id}`, { method: 'DELETE' });
export const resetAdminBuyerPassword = (id, data) => request(`/api/v1/admin/buyers/${id}/reset-password`, { method: 'POST', body: JSON.stringify(data) });
export const impersonateBuyer = (data) => request('/api/v1/admin/impersonate', { method: 'POST', body: JSON.stringify(data) });
export const stopImpersonate = () => request('/api/v1/admin/impersonate/stop', { method: 'POST' });
